"use client"

import { Clock, Shield, FileText, Scale, AlertTriangle, Users, ShieldCheck } from "lucide-react"
import { motion, useInView } from "framer-motion"
import { useRef, useState, useEffect } from "react"
import { Menu } from "lucide-react"
import { useAuth } from "../contexts/AuthContext"
import { StickyFooter } from "../components/sticky-footer"
import axios from "axios"
import { toast } from "sonner"

const Privacy = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })
  const [isEmailHovering, setIsEmailHovering] = useState(false)
  const [isPhoneHovering, setIsPhoneHovering] = useState(false)
  const [isLocationHovering, setIsLocationHovering] = useState(false)
  const [isFormHovering, setIsFormHovering] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const amplyLogo = new URL("../assets/amply.png", import.meta.url).href
  const [isScrolled, setIsScrolled] = useState(false)
  const user = useAuth()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()

    const name = e.target.name.value
    const email = e.target.email.value
    const artistName = e.target.artistName.value
    const message = e.target.message.value
    const turnstileToken = e.target["cf-turnstile-response"].value

    if (!name || !email || !message) {
      alert("Please fill in all required fields.")
      return
    }

    if (!turnstileToken) {
      alert("Please complete the CAPTCHA.")
      return
    }

    try {
  const response = await axios.post("https://backend.amply.buildfast.us/send-email", {
        email,
        subject: `Contact Form Submission from ${name}`,
        message: `Name: ${name}\nArtist/Band Name: ${artistName}\nMessage: ${message}`,
        turnstileToken,
      })

      if (response.data.success) {
        toast.success("Message sent successfully!")
        e.target.reset()
      } else {
        toast.error("Failed to send message. Please try again later.")
      }
    } catch (error) {
      console.error("Error sending message:", error)
      toast.error("An error occurred. Please try again later.")
    }
  }

  useEffect(() => {
    const script = document.createElement("script")
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js"
    script.async = true
    document.body.appendChild(script)
    return () => {
      document.body.removeChild(script)
    }
  }, [])

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Desktop Header */}
      <header
        className={`sticky top-4 z-[9999] mx-auto hidden w-full flex-row items-center justify-between self-start rounded-full bg-transparent md:flex backdrop-blur-sm border border-[#ebcebc] shadow-lg transition-all duration-300 ${
          isScrolled ? "max-w-3xl px-2" : "max-w-5xl px-4"
        } py-2`}
        style={{
          willChange: "transform",
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
          perspective: "1000px",
        }}
      >
        <a
          className={`z-50 flex items-center justify-center gap-2 transition-all duration-300 ${
            isScrolled ? "ml-4" : ""
          }`}
          href="/"
        >
          <img src={amplyLogo || "/placeholder.svg"} className="w-8 h-8" alt="Amply Logo" />
        </a>

        <div className="absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium text-white transition duration-200 hover:text-white md:flex md:space-x-2">
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="./#features"
          >
            <span style={{ fontWeight: "bold" }} className="relative z-20">
              Features
            </span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/about"
          >
            <span style={{ fontWeight: "bold" }} className="relative z-20">
              About
            </span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/pricing"
          >
            <span style={{ fontWeight: "bold" }} className="relative z-20">
              Pricing
            </span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/contact"
          >
            <span style={{ fontWeight: "bold" }} className="relative z-20">
              Contact Us
            </span>
          </a>
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <a
              href="/dashboard"
              className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
            >
              Dashboard
            </a>
          ) : (
            <>
              <a
                href="/login"
                className="rounded-full border border-[#e3c5b3] font-bold relative cursor-pointer transition duration-200 inline-block text-center text-primary-foreground px-4 py-2 text-sm"
              >
                Log In
              </a>
              <a
                href="/register"
                className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
              >
                Sign Up
              </a>
            </>
          )}
        </div>
      </header>

      {/* Mobile Header */}
      <header className="sticky top-4 z-[9999] mx-4 flex w-auto flex-row items-center justify-between rounded-full bg-transparent backdrop-blur-sm border border-[#ebcebc] shadow-lg md:hidden px-4 py-3">
        <a className="flex items-center justify-center gap-2" href="./">
          <img src={amplyLogo || "/placeholder.svg"} className="w-8 h-8" alt="Amply Logo" />
        </a>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-background/50 border border-border/50 transition-colors hover:bg-background/80"
          aria-label="Toggle menu"
        >
          <div className="flex flex-col items-center justify-center w-5 h-5 space-y-1">
            {isMobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <Menu className="w-5 h-5 text-foreground" />
            )}
          </div>
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[9998] bg-transparent backdrop-blur-sm md:hidden">
          <div className="absolute top-28 left-4 right-4 bg-transparent/80 backdrop-blur-md border border-border/50 rounded-2xl shadow-2xl p-6">
            <nav className="flex flex-col space-y-4">
              <a
                href="./#features"
                className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
              >
                Features
              </a>
              <a
                href="/about"
                className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
              >
                About
              </a>
              <a
                href="/pricing"
                className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
              >
                Pricing
              </a>
              <a
                href="/contact"
                className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
              >
                Contact Us
              </a>
              <div className="border-t border-border/50 pt-4 mt-4 flex flex-col space-y-3">
                {user ? (
                  <a
                    href="/dashboard"
                    className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
                  >
                    Dashboard
                  </a>
                ) : (
                  <>
                    <a
                      href="/login"
                      className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
                    >
                      Log In
                    </a>
                    <a
                      href="/register"
                      className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
                    >
                      Sign Up
                    </a>
                  </>
                )}
              </div>
            </nav>
          </div>
        </div>
      )}
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-black">
        <div className="absolute inset-0 bg-[url('/abstract-music-waves-pattern.jpg')] opacity-5"></div>
        <div className="pointer-events-none absolute left-1/2 top-0 z-0 h-80 w-[60vw] -translate-x-1/2 bg-gradient-radial from-[#e3c5b3]/30 via-[#e3c5b3]/10 to-transparent opacity-60 blur-2xl"></div>
        <div className="absolute top-0 left-1/2 w-full max-w-4xl -translate-x-1/2 h-[2px]">
          <div
            className="w-full h-full bg-gradient-to-r from-transparent via-[#e3c5b3] to-transparent animate-pulse"
            style={{ opacity: 0.5 }}
          />
        </div>
        <br />
        <br />
        <br />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="flex justify-center mb-8"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#e3c5b3]/20 rounded-full blur-xl"></div>
                <div className="relative bg-[#e3c5b3]/10 p-6 rounded-full">
                  <ShieldCheck className="h-16 w-16 text-[#e3c5b3]" />
                </div>
              </div>
            </motion.div>
            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-balance mb-6 text-white"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{
                textShadow: "0 0 16px #e3c5b3, 0 0 4px white",
              }}
            >
              Privacy <span className="text-[#e3c5b3]">Policy</span>
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-white/70 text-balance max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Please read this privacy Policy carefully before using Amply's services
            </motion.p>
          </motion.div>
        </div>
      </section>
      <br />


      {/* Privacy Policy Section */}
      <section className="text-white relative overflow-hidden py-12 sm:py-24 md:py-32 bg-black/50">
        <div className="pointer-events-none absolute left-1/2 top-0 z-0 h-80 w-[60vw] -translate-x-1/2 bg-gradient-radial from-[#e3c5b3]/30 via-[#e3c5b3]/10 to-transparent opacity-60 blur-2xl"></div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8"
        >
          <div className="grid gap-8 lg:gap-12">
            <motion.div
              className="text-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#e3c5b3]/10 border border-[#e3c5b3]/30">
                <Clock className="w-4 h-4 text-[#e3c5b3]" />
                <span className="text-sm text-white/70">Effective Date: September 18, 2025</span>
              </div>
            </motion.div>
            <div className="space-y-12">
              {/* Introduction */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">Privacy Policy</h3>
                <p className="text-white/70 leading-relaxed">
                  Amply values your privacy. This Privacy Policy explains how we collect, use, and protect your information when you access or use our products, services, and website (collectively, the “Services”).
                </p>
              </motion.div>
              {/* Section 1 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h3>
                <ul className="list-disc list-inside space-y-2 ml-4 text-white/70">
                  <li><strong>Account Information:</strong> Name, email address, username, password, and profile details you provide when creating an account.</li>
                  <li><strong>Usage Data:</strong> Information about how you interact with our Services, such as features used, pages visited, and activity logs.</li>
                  <li><strong>Device & Technical Data:</strong> IP address, browser type, operating system, device identifiers, and cookies.</li>
                  <li><strong>Payment Information (if applicable):</strong> When you purchase subscriptions or services, we collect payment details through our secure third-party payment processor (e.g., Stripe). Amply does not store full payment card details.</li>
                  <li><strong>User Content:</strong> Any messages, files, or media you share through the Services.</li>
                </ul>
              </motion.div>
              {/* Section 2 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">2. How We Use Information</h3>
                <ul className="list-disc list-inside space-y-2 ml-4 text-white/70">
                  <li>Provide, operate, and improve the Services.</li>
                  <li>Personalize your experience and recommend relevant features.</li>
                  <li>Process payments and manage subscriptions.</li>
                  <li>Communicate with you, including support, updates, and marketing (where permitted).</li>
                  <li>Monitor and enhance security, prevent fraud, and ensure compliance with legal obligations.</li>
                </ul>
              </motion.div>
              {/* Section 3 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">3. Sharing of Information</h3>
                <ul className="list-disc list-inside space-y-2 ml-4 text-white/70">
                  <li>We do not sell your personal data. We may share your information only in these cases:</li>
                  <li><strong>Service Providers:</strong> With vendors who help us operate the Services (e.g., hosting, payment processing, analytics).</li>
                  <li><strong>Legal Compliance:</strong> If required by law, regulation, or legal process.</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
                  <li><strong>With Your Consent:</strong> When you explicitly authorize us to share information.</li>
                </ul>
              </motion.div>
              {/* Section 4 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">4. Cookies & Tracking Technologies</h3>
                <p className="text-white/70 leading-relaxed">
                  We use cookies and similar technologies to improve functionality, analyze usage, and provide a personalized experience. You can control cookies through your browser settings, but disabling them may limit your use of certain features.
                </p>
              </motion.div>
              {/* Section 5 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">5. Data Retention</h3>
                <p className="text-white/70 leading-relaxed">
                  We retain personal information only as long as necessary to provide the Services, comply with legal obligations, or resolve disputes.
                </p>
              </motion.div>
              {/* Section 6 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">6. Security</h3>
                <p className="text-white/70 leading-relaxed">
                  We use reasonable technical, administrative, and organizational measures to protect your data. However, no system is completely secure, and we cannot guarantee absolute protection.
                </p>
              </motion.div>
              {/* Section 7 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.9 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">7. Your Rights</h3>
                <ul className="list-disc list-inside space-y-2 ml-4 text-white/70">
                  <li>Depending on your location, you may have certain privacy rights, including:</li>
                  <li>Accessing the personal information we hold about you.</li>
                  <li>Requesting corrections or updates.</li>
                  <li>Requesting deletion of your data.</li>
                  <li>Opting out of marketing communications.</li>
                  <li>Data portability and restriction of processing (where applicable under GDPR or similar laws).</li>
                </ul>
                <p className="text-white/70 leading-relaxed mt-2">To exercise your rights, contact us at:</p>
              </motion.div>
              {/* Section 8 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.0 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">8. Children’s Privacy</h3>
                <p className="text-white/70 leading-relaxed">
                  Our Services are not directed to children under 13 (or the minimum age of consent in your country). We do not knowingly collect personal information from children.
                </p>
              </motion.div>
              {/* Section 9 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.1 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">9. International Users</h3>
                <p className="text-white/70 leading-relaxed">
                  If you access the Services from outside the United States, note that your information may be transferred, stored, and processed in a country where privacy laws may differ from your own.
                </p>
              </motion.div>
              {/* Section 10 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.2 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">10. Changes to This Policy</h3>
                <p className="text-white/70 leading-relaxed">
                  We may update this Privacy Policy from time to time. If changes are material, we will notify you by updating the “Effective Date” or through direct communication.
                </p>
              </motion.div>
              {/* Section 11 */}
              <motion.div
                className="group border-[#e3c5b3]/40 text-white relative flex flex-col overflow-hidden rounded-xl border-2 p-8 shadow-xl bg-black/50"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.3 }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">11. Contact Us</h3>
                <p className="text-white/70 leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us at:<br />
                  <span className="font-semibold">Email:</span> <a href="mailto:support@amply.com" className="underline">support@amply.com</a>
                </p>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </section>
      <div className="h-[500px]" />
      <StickyFooter />
    </div>
  )
}

export default Privacy
